// How an import's result rows split, for the progress box and for the "Assign to" pick.
//
// A row is new ('created'), failed, or points at a lead that already exists: merged
// ('updated', an extra policy for that person) or an exact duplicate ('skipped'). Those
// last two carry that lead's id. If this same upload created it, the row is just the same
// person twice in the file; otherwise the person was already in your leads, and can still
// be handed to the picked agent.

export type ImportResultRow = {
  action?: string;
  status?: string;
  entityId?: string | null;
  message?: string;
  rawDataSnapshot?: any;
};

const FAILED_STATUSES = new Set(["failure", "failed", "error"]);

export function summarizeImportResults(results: ImportResultRow[]) {
  const isFailed = (r: ImportResultRow) => r.action === "failed" || FAILED_STATUSES.has(String(r.status));
  const failed = results.filter(isFailed);
  const created = results.filter((r) => !isFailed(r) && r.action === "created");
  const matched = results.filter((r) => !isFailed(r) && (r.action === "updated" || r.action === "skipped"));

  const ids = (rows: ImportResultRow[]) =>
    Array.from(new Set(rows.map((r) => r.entityId).filter(Boolean).map(String)));
  const createdIds = ids(created);
  const createdSet = new Set(createdIds);
  const repeatsInFile = matched.filter((r) => r.entityId && createdSet.has(String(r.entityId)));
  const alreadyInLeads = matched.filter((r) => !(r.entityId && createdSet.has(String(r.entityId))));
  // Several rows can point at one existing lead (the same person twice in the file).
  const existingIds = ids(alreadyInLeads);

  return { created, alreadyInLeads, repeatsInFile, failed, createdIds, existingIds, total: results.length };
}
